import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  SuccessPortfolioCovenantGQLType,
  ReportingAccessorEntityAttributesGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { useGetSuccessPortfolioCovenantByIdQuery } from '~/packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useGetCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import {
  getThresholdType,
  portfolioCovenantValueField,
} from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantValue/thresholdValueUtils';
import { CovenantChartDetails } from '~/packages/portfolio/ui/covenants/CovenantChartDetails';
import { expandCovenantsChartData } from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import {
  getInputResultsFromPortfolioCovenantResults,
  getRecurringDatesFromPortfolioCovenantResults,
} from '~/packages/settings/ui/SettingsFacilityPortfolioCovenantsPreviewDrawer';
import {
  PortfolioCovenantPreviewResultsInputsAccordion,
  PortfolioCovenantPreviewResultsRecurringDatesAccordion,
} from '~/packages/settings/ui/SettingsFacilityPortfolioCovenantsPreviewDrawerComponents';

type DigitalMonthlyReportPortfolioCovenantOverviewCardProps = {
  portfolioCovenant: SuccessPortfolioCovenantGQLType;
};
const DigitalMonthlyReportPortfolioCovenantOverviewCard = ({
  portfolioCovenant,
}: DigitalMonthlyReportPortfolioCovenantOverviewCardProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    isOpen: isInputsDrawerOpen,
    onOpen: onInputsDrawerOpen,
    onClose: onInputsDrawerClose,
  } = useDisclosure();

  const creditAgreementIdUsedForPortfolioCovenant =
    portfolioCovenant.creditAgreementId;

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCreditAgreementFullArtifactQuery({
    variables: { creditAgreementId: creditAgreementIdUsedForPortfolioCovenant },
  });

  const creditAgreementPortfolioCovenant: Util.Maybe<{
    portfolioCovenantFullPropertyKey: string;
    portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  }> = useMemo(() => {
    let parsedCreditAgreementFullArtifact = null;
    if (
      !creditAgreementData ||
      !creditAgreementData.creditAgreementFullArtifact
    ) {
      return null;
    }

    try {
      const {
        creditAgreement: creditAgreementRaw,
      } = creditAgreementData.creditAgreementFullArtifact;
      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementRaw,
      );
      parsedCreditAgreementFullArtifact = parsedCreditAgreement;
    } catch (err) {
      const errorToastTitle = `Error parsing credit agreement for portfolio covenant [${portfolioCovenant.covenantKey}]`;
      toast({
        ...errorToastConfig,
        title: errorToastTitle,
        id: 'errorParsingCreditAgreement',
      });
      navigate('/');
    }

    if (!parsedCreditAgreementFullArtifact) {
      return null;
    }

    const creditAgreementPortfolioCovenantIndex = parsedCreditAgreementFullArtifact.portfolioCovenants.findIndex(
      (creditAgreementPortCov) =>
        creditAgreementPortCov.covenantKey === portfolioCovenant.covenantKey,
    );

    return {
      portfolioCovenantFullPropertyKey: `portfolioCovenants[${creditAgreementPortfolioCovenantIndex}]`,
      portfolioCovenant:
        parsedCreditAgreementFullArtifact.portfolioCovenants[
          creditAgreementPortfolioCovenantIndex
        ],
    };
  }, [creditAgreementData, navigate, toast, portfolioCovenant.covenantKey]);

  const thresholdType = useMemo(() => {
    return creditAgreementPortfolioCovenant &&
      creditAgreementPortfolioCovenant.portfolioCovenant._config_
      ? getThresholdType(
          'portfolio',
          creditAgreementPortfolioCovenant.portfolioCovenant._config_
            .intermediateFields,
        )
      : null;
  }, [creditAgreementPortfolioCovenant]);

  const portfolioCovenantValueFormula = useMemo(() => {
    return creditAgreementPortfolioCovenant &&
      creditAgreementPortfolioCovenant.portfolioCovenant._config_
      ? creditAgreementPortfolioCovenant.portfolioCovenant._config_.intermediateFields.find(
          (intermediateField) =>
            intermediateField.field === portfolioCovenantValueField,
        )?.config.formula
      : null;
  }, [creditAgreementPortfolioCovenant]);

  const expandedPortfolioCovenantDataPoint = useMemo(() => {
    if (!portfolioCovenant) {
      return null;
    }

    return expandCovenantsChartData([portfolioCovenant]);
  }, [portfolioCovenant]);

  const recurringDatesVariableResults = useMemo(() => {
    if (!portfolioCovenant || !creditAgreementPortfolioCovenant) {
      return null;
    }

    return getRecurringDatesFromPortfolioCovenantResults({
      portfolioCovenantPreviewResult: portfolioCovenant,
      creditAgreementPortfolioCovenant:
        creditAgreementPortfolioCovenant.portfolioCovenant,
    });
  }, [creditAgreementPortfolioCovenant, portfolioCovenant]);

  const inputVariableResults = useMemo(() => {
    if (!portfolioCovenant || !creditAgreementPortfolioCovenant) {
      return null;
    }

    return getInputResultsFromPortfolioCovenantResults({
      portfolioCovenantPreviewResult: portfolioCovenant,
      creditAgreementPortfolioCovenant:
        creditAgreementPortfolioCovenant.portfolioCovenant,
    });
  }, [creditAgreementPortfolioCovenant, portfolioCovenant]);

  if (!creditAgreementData || isCreditAgreementLoading) {
    return <Skeleton height="400px" />;
  }

  if (expandedPortfolioCovenantDataPoint) {
    return (
      <Box>
        <Flex alignItems="baseline">
          <Heading as="h2" fontSize="24px" mb="32px">
            {commonUtils.toTitleCase(portfolioCovenant.covenantKey)}
          </Heading>
          <Spacer />
          <Button
            variant="link"
            onClick={onInputsDrawerOpen}
            color={colors.info}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
          >
            View Inputs
          </Button>
        </Flex>
        <CovenantChartDetails
          selectedDataPoint={expandedPortfolioCovenantDataPoint[0]}
          thresholdType={thresholdType}
          covenantValueFormula={portfolioCovenantValueFormula}
        />
        {creditAgreementPortfolioCovenant && (
          <Drawer
            isOpen={isInputsDrawerOpen}
            placement="right"
            onClose={onInputsDrawerClose}
            size="xl"
          >
            <DrawerOverlay />
            <DrawerContent
              padding="16px"
              backgroundColor={colors.background[100]}
            >
              <DrawerHeader>
                <Flex alignItems="center" mb="24px">
                  <Heading
                    as="h2"
                    fontSize="24px"
                    lineHeight="32px"
                    fontWeight={600}
                  >
                    {`${portfolioCovenant.covenantKey} Inputs`}
                  </Heading>
                  <Spacer />
                  <Button variant="ghost" onClick={onInputsDrawerClose} p="0">
                    <Icon icon="x" />
                  </Button>
                </Flex>
              </DrawerHeader>

              <DrawerBody>
                {recurringDatesVariableResults && (
                  <PortfolioCovenantPreviewResultsRecurringDatesAccordion
                    recurringDatesResults={recurringDatesVariableResults}
                    creditAgreementPortfolioCovenant={
                      creditAgreementPortfolioCovenant.portfolioCovenant
                    }
                    portfolioCovenantFullPropertyKey={
                      creditAgreementPortfolioCovenant.portfolioCovenantFullPropertyKey
                    }
                  />
                )}
                {inputVariableResults && (
                  <PortfolioCovenantPreviewResultsInputsAccordion
                    inputResults={inputVariableResults}
                    creditAgreementPortfolioCovenant={
                      creditAgreementPortfolioCovenant.portfolioCovenant
                    }
                  />
                )}
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        )}
      </Box>
    );
  }

  return null;
};

type DigitalMonthlyReportPortfolioCovenantOverviewCardContainerProps = {
  portfolioCovenantId: string;
};
const DigitalMonthlyReportPortfolioCovenantOverviewCardContainer = ({
  portfolioCovenantId,
}: DigitalMonthlyReportPortfolioCovenantOverviewCardContainerProps) => {
  const {
    data: portfolioCovenantData,
    loading: isPortfolioCovenantLoading,
  } = useGetSuccessPortfolioCovenantByIdQuery({
    variables: { portfolioCovenantId },
  });

  if (
    !portfolioCovenantData ||
    isPortfolioCovenantLoading ||
    !portfolioCovenantData.getSuccessPortfolioCovenantById
  ) {
    return <Skeleton height="400px" />;
  }

  return (
    <DigitalMonthlyReportPortfolioCovenantOverviewCard
      portfolioCovenant={portfolioCovenantData.getSuccessPortfolioCovenantById}
    />
  );
};

type DigitalMonthlyReportPortfolioCovenantOverviewProps = {
  portfolioCovenantAttributes: ReportingAccessorEntityAttributesGQLType[];
};
export const DigitalMonthlyReportPortfolioCovenantOverview = ({
  portfolioCovenantAttributes,
}: DigitalMonthlyReportPortfolioCovenantOverviewProps) => {
  return (
    <VStack spacing="40px">
      {portfolioCovenantAttributes.map((portfolioCovenantAttribute) => {
        const {
          supplementalEntityKey: covenantKey,
          identifier,
        } = portfolioCovenantAttribute;

        const portfolioCovenantId = commonUtils.parseDbPgIdPointer(identifier);

        if (!covenantKey || !portfolioCovenantId) {
          return null;
        }

        return (
          <Box
            key={`portcov-overview-card-${covenantKey}`}
            backgroundColor="white"
            borderRadius="8px"
            borderWidth="1px"
            borderColor={colors.gray[200]}
            padding="40px"
            w="100%"
          >
            <DigitalMonthlyReportPortfolioCovenantOverviewCardContainer
              portfolioCovenantId={portfolioCovenantId}
            />
          </Box>
        );
      })}
    </VStack>
  );
};
